import { useCallback } from "react";
import { ServerMessages, useSocketContext } from "../../context/SocketContext";
import { Barrier, CreateBarrierPayload } from "../../types/Barrier";
import { eventBus } from "../../common/eventBus";

const useBarriersService = () => {
    const { emit } = useSocketContext();

    const getAllBarriers = useCallback(async (): Promise<Barrier[]> => {
        const response = await emit({
            message: ServerMessages.getAllBarriers,
        });

        return response?.data ?? [];
    }, [emit]);

    const createBarrier = useCallback(async (payload: Partial<CreateBarrierPayload>): Promise<Barrier> => {
        const response = await emit<Barrier>({
            message: ServerMessages.createBarrier,
            barrier: payload,
        });

        if (!response || response.data == null) {
            throw new Error('Error: no data returned from server');
        }

        eventBus.emit('barrierCreated', response.data);
        return response.data;
    }, [emit]);

    const updateBarrier = useCallback(async (payload: Partial<Barrier>): Promise<Barrier> => {
        const response = await emit<Barrier>({
            message: ServerMessages.updateBarrier,
            barrier: payload,
        });

        if (!response || response.data == null) {
            throw new Error('Error: no data returned from server');
        }

        eventBus.emit('barrierEdited', response.data);
        return response.data;
    }, [emit]);

    const deleteBarrier = useCallback(async (barrierId: number): Promise<void> => {
        const response = await emit({
            message: ServerMessages.deleteBarrier,
            barrierId,
        });

        eventBus.emit('barrierDeleted', barrierId);
        return response?.data;
    }, [emit]);

    const triggerBarrier = useCallback(async (barrierId: number): Promise<void> => {
        const response = await emit({
            message: ServerMessages.triggerBarrier,
            barrierId,
        });

        return response?.data;
    }, [emit]);

    return {
        getAllBarriers,
        createBarrier,
        updateBarrier,
        deleteBarrier,
        triggerBarrier,
    };
};

export default useBarriersService;
